import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { useAuth } from "@/lib/auth";

/**
 * Gate a route on the authenticated user. While the initial `/auth/me` probe
 * is in flight we show a minimal centered spinner — jumping straight to
 * `/login` would flash the login form every refresh even for valid sessions.
 *
 * Multi-tenant (plan §6): if the caller's `mustChangePassword` flag is set,
 * route them to `/change-password` unless they are already on that page or
 * signing out. The flag is server-derived on every /auth/me + sliding
 * refresh, so once the user completes the reset the next refresh releases
 * the gate automatically.
 */
const PASSWORD_CHANGE_ALLOWED_PATHS = new Set<string>([
  "/change-password",
  "/logout",
]);

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text-muted">
        <Loader2
          className="h-6 w-6 animate-spin"
          strokeWidth={1.5}
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (
    mustChangePassword &&
    !PASSWORD_CHANGE_ALLOWED_PATHS.has(location.pathname)
  ) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
